import React, { useState, useRef, useEffect } from 'react';
import { FiCamera, FiX, FiCheck, FiTrash2, FiRotateCcw, FiPlus, FiMaximize, FiCreditCard, FiChevronDown } from 'react-icons/fi';
import IconWrapper from './IconWrapper';

// Update interface to include mode options
interface CameraCaptureModalProps {
  onClose: () => void;
  onCapture: (images: File[], mode?: 'idcard' | 'normal') => void;
}

type CaptureMode = 'portrait' | 'landscape' | 'idcard';

const CameraCaptureModal: React.FC<CameraCaptureModalProps> = ({ onClose, onCapture }) => {
  // Basic state
  const [capturedImages, setCapturedImages] = useState<File[]>([]);
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [captureMode, setCaptureMode] = useState<CaptureMode>('portrait');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  // Refs
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  // Initialize camera on component mount
  useEffect(() => {
    console.log("CameraCaptureModal mounted - starting camera");
    startCamera();
    
    // Clean up on unmount
    return () => {
      console.log("CameraCaptureModal unmounting - cleaning up");
      stopCameraStream();
      cleanupImageUrls();
    };
  }, []);

  // Clean up URLs to prevent memory leaks
  const cleanupImageUrls = () => {
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    
    // Clean up thumbnail URLs
    document.querySelectorAll('[data-thumbnail-url]').forEach(img => {
      const url = img.getAttribute('data-thumbnail-url');
      if (url) URL.revokeObjectURL(url);
    });
  };

  // Explicitly stop the camera stream
  const stopCameraStream = () => {
    console.log("Stopping camera stream");
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        console.log(`Stopping track: ${track.kind}`);
        track.stop();
      });
      streamRef.current = null;
    }
    setIsCameraActive(false);
  };

  // Start or restart the camera
  const startCamera = async () => {
    console.log("Starting camera");
    try {
      // Stop any existing stream first
      stopCameraStream();
      
      // Request camera access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        }
      });
      
      console.log("Camera stream obtained:", stream.getTracks().length, "tracks");
      streamRef.current = stream;
      
      // Connect stream to video element
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Promise-based play to handle autoplay restrictions
        videoRef.current.onloadedmetadata = () => {
          if (videoRef.current) {
            videoRef.current.play().then(() => {
              console.log("Video is playing");
              setIsCameraActive(true);
              setCameraError(null);
            }).catch(err => {
              console.error("Error playing video:", err);
              setCameraError("Could not play video feed. Please check permissions.");
            });
          }
        };
      } else {
        console.error("Video ref is null");
        setCameraError("Camera component not initialized properly, Please reload the page.");
      }
      
      // Clear any preview when starting camera
      setPreviewImageUrl(null);
    } catch (error) {
      console.error("Error accessing camera:", error);
      setCameraError("Could not access camera. Please make sure camera permissions are granted.");
      setIsCameraActive(false);
    }
  };

  // Replace toggleMode with selectMode function
  const selectMode = (mode: CaptureMode) => {
    console.log("Changing mode to:", mode);
    setCaptureMode(mode);
    setIsDropdownOpen(false);
  };

  // Get icon for current mode
  const getModeIcon = () => {
    switch(captureMode) {
      case 'portrait': return <IconWrapper icon={FiMaximize} className="mr-2" />;
      case 'landscape': return <IconWrapper icon={FiMaximize} className="mr-2 rotate-90" />;
      case 'idcard': return <IconWrapper icon={FiCreditCard} className="mr-2" />;
      default: return <IconWrapper icon={FiMaximize} className="mr-2" />;
    }
  };

  // Get label for current mode
  const getModeLabel = () => {
    switch(captureMode) {
      case 'portrait': return "Portrait";
      case 'landscape': return "Landscape";
      case 'idcard': return "ID Card";
      default: return "Portrait";
    }
  };

  // Function to get the aspect ratio based on current mode
  const getAspectRatio = () => {
    if (captureMode === 'landscape') return '1.414 / 1';
    if (captureMode === 'idcard') return '1.586 / 1'; // Standard ID card ratio (85.6mm × 54mm)
    return '1 / 1.414'; // portrait
  };

  // Function to capture image from video stream
  const captureImage = () => {
    console.log("Capture button clicked");
    if (!videoRef.current || !canvasRef.current || !isCameraActive) {
      console.error("Cannot capture: video not active or canvas not available");
      return;
    }
    
    // Check if we've reached the limit for ID card mode
    if (captureMode === 'idcard' && capturedImages.length >= 2) {
      console.log("ID card mode: maximum 2 images allowed");
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Get video dimensions
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    
    console.log(`Video dimensions: ${videoWidth}x${videoHeight}`);
    
    if (videoWidth === 0 || videoHeight === 0) {
      console.error("Video dimensions are zero");
      return;
    }
    
    // Calculate crop dimensions based on mode
    let cropWidth, cropHeight;
    
    if (captureMode === 'landscape') {
      // Landscape A4
      cropHeight = Math.min(videoHeight, videoWidth / 1.414);
      cropWidth = cropHeight * 1.414;
    } else if (captureMode === 'idcard') {
      // ID Card (CR80 standard ratio)
      cropHeight = Math.min(videoHeight, videoWidth / 1.586);
      cropWidth = cropHeight * 1.586;
    } else {
      // Portrait A4
      cropWidth = Math.min(videoWidth, videoHeight / 1.414);
      cropHeight = cropWidth * 1.414;
    }
    
    // Center the crop
    const cropX = (videoWidth - cropWidth) / 2;
    const cropY = (videoHeight - cropHeight) / 2;
    
    // Set canvas size to match crop dimensions
    canvas.width = cropWidth;
    canvas.height = cropHeight;
    
    // Draw the cropped video frame to canvas
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error("Failed to get canvas context");
      return;
    }
    
    try {
      // Draw the cropped portion of the video to the canvas
      ctx.drawImage(
        video,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, cropWidth, cropHeight
      );
      
      // Convert canvas to a file
      canvas.toBlob((blob) => {
        if (blob) {
          // Create a File from the Blob
          const timestamp = new Date().getTime();
          const filename = captureMode === 'idcard'
            ? `idcard_${capturedImages.length === 0 ? 'front' : 'back'}_${timestamp}.jpg`
            : `camera_capture_${timestamp}.jpg`;
          
          const newFile = new File(
            [blob],
            filename,
            { type: 'image/jpeg' }
          );
          
          // Add to captured images
          setCapturedImages(prev => [...prev, newFile]);
          
          // Create a preview URL and display it
          const imageUrl = URL.createObjectURL(blob);
          setPreviewImageUrl(imageUrl);
          
          // Stop camera after capturing image
          stopCameraStream();
          
          console.log("Image captured successfully");
        } else {
          console.error("Failed to create image blob");
        }
      }, 'image/jpeg', 0.92);
    } catch (err) {
      console.error("Error capturing image:", err);
    }
  };

  // Function to handle "Add Photo" button
  const handleAddPhoto = () => {
    console.log("Add photo - clearing preview");
    
    // Clear the preview image
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
    
    // Restart the camera
    startCamera();
  };

  // Function to handle "Retake" button - remove the last image and return to camera
  const handleRetake = () => {
    console.log("Retake - clearing preview and removing last image");
    
    // Clear the preview image
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
    
    // Remove the latest image when retaking
    if (capturedImages.length > 0) {
      // Create a copy without the last element
      const newImages = [...capturedImages];
      newImages.pop();
      setCapturedImages(newImages);
    }
    
    // Restart the camera
    startCamera();
  };

  // Remove an image from the captured list
  const handleRemoveImage = (index: number) => {
    setCapturedImages(prev => prev.filter((_, i) => i !== index));
  };

  // Finish capturing and return images
  const handleDoneCapturing = () => {
    // Stop the camera
    stopCameraStream();
    
    // Clear preview
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
    }
    
    // Pass captured images back to parent with mode info
    console.log("Done capturing, mode:", captureMode, "Images:", capturedImages.length);
    
    // Always explicitly pass the mode to ensure it's correctly handled
    if (captureMode === 'idcard') {
      console.log("Passing ID card mode to callback");
      onCapture(capturedImages, 'idcard');
    } else if (captureMode === 'landscape') {
      console.log("Passing landscape mode to callback");
      onCapture(capturedImages, 'normal');
    } else {
      console.log("Passing portrait mode to callback");
      onCapture(capturedImages, 'normal');
    }
  };

  // Get the appropriate caption for the next photo in ID card mode
  const getIdCardCaption = () => {
    if (capturedImages.length === 0) return "Capture front side of ID";
    if (capturedImages.length === 1) return "Capture back side of ID";
    return "";
  };

  // Determine if capture button should be disabled
  const isCaptureDisabled = () => {
    if (!isCameraActive) return true;
    if (captureMode === 'idcard' && capturedImages.length >= 2) return true;
    return false;
  };

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center p-2">
      <div className="bg-white rounded-lg shadow-xl overflow-hidden w-full max-w-md h-[85vh] flex flex-col">
        {/* Header - reduced padding and text size */}
        <div className="p-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white flex justify-between items-center">
          <h3 className="font-medium text-sm flex items-center">
            <IconWrapper icon={FiCamera} className="mr-1" />
            {captureMode === 'idcard' ? 'ID Card Scanner' : 'Document Scanner'}
          </h3>
          <div className="flex space-x-1">
            {/* Replace toggle button with dropdown */}
            <div className="relative">
                <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="transition-all rounded-md text-gray-700 border border-blue-700 bg-white px-2 py-1 flex items-center text-xs"
                aria-haspopup="true"
                aria-expanded={isDropdownOpen}
                >
                {getModeIcon()}
                <span className="mr-1">{getModeLabel()}</span>
                <IconWrapper icon={FiChevronDown} className="text-xs ml-1" />
              </button>
              
              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <>
                  {/* Backdrop to close dropdown when clicking outside */}
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setIsDropdownOpen(false)}
                  ></div>
                  
                  {/* Dropdown content */}
                  <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-20 overflow-hidden py-1 border border-gray-200">
                    <h5 className="text-xs font-medium text-gray-700 px-3 py-1 border-b border-gray-100 mb-1">Select Capture Mode</h5>
                    <button 
                      className={`w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 flex items-center text-xs ${captureMode === 'portrait' ? 'bg-blue-50' : ''}`}
                      onClick={() => selectMode('portrait')}
                    >
                      <IconWrapper icon={FiMaximize} className="mr-2 text-blue-600" />
                      <div>
                        <div>Portrait</div>
                        <div className="text-gray-400 text-[10px]">For standard portrait documents</div>
                      </div>
                    </button>
                    <button 
                      className={`w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 flex items-center text-xs ${captureMode === 'landscape' ? 'bg-blue-50' : ''}`}
                      onClick={() => selectMode('landscape')}
                    >
                      <IconWrapper icon={FiMaximize} className="mr-2 rotate-90 text-blue-600" />
                      <div>
                        <div>Landscape</div>
                        <div className="text-gray-400 text-[10px]">For wide documents and certificates</div>
                      </div>
                    </button>
                    <button 
                      className={`w-full text-left px-3 py-2 text-gray-700 hover:bg-blue-50 flex items-center text-xs ${captureMode === 'idcard' ? 'bg-blue-50' : ''}`}
                      onClick={() => selectMode('idcard')}
                    >
                      <IconWrapper icon={FiCreditCard} className="mr-2 text-blue-600" />
                      <div>
                        <div>ID Card</div>
                        <div className="text-gray-400 text-[10px]">For capturing both sides of an ID</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>
            
            <button 
              onClick={onClose} 
              className="rounded-full hover:bg-white hover:bg-opacity-20 p-1.5"
              aria-label="Close"
            >
              <IconWrapper icon={FiX} className="text-lg" />
            </button>
          </div>
        </div>
        
        {/* Main content */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="flex flex-col h-full">
            {cameraError ? (
              <div className="flex-1 flex items-center justify-center p-2 bg-gray-50">
                <div className="text-red-500 p-4 text-center bg-white rounded-lg shadow">
                  <p className="font-medium mb-2 text-sm">{cameraError}</p>
                  <button 
                    onClick={startCamera}
                    className="mt-2 px-3 py-1.5 bg-blue-500 text-white rounded-lg block mx-auto hover:bg-blue-600 transition-colors text-sm"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex flex-col h-full">
                {/* Camera/Preview Area - reduced padding */}
                <div className="flex-grow bg-black p-0.5 flex justify-center items-center">
                  <div 
                    className="relative overflow-hidden"
                    style={{ 
                      aspectRatio: getAspectRatio(),
                      width: '95%',
                      maxWidth: '95%', 
                      maxHeight: '60vh'  // Reduced from 70vh to ensure buttons are visible
                    }}
                  >
                    {/* Always show the frame */}
                    <div className="absolute inset-0 border-2 border-dashed border-white z-10"></div>
                    
                    {/* Show ID card guidance text */}
                    {captureMode === 'idcard' && !previewImageUrl && (
                      <div className="absolute top-1 left-0 right-0 text-center text-white text-xs font-medium bg-black bg-opacity-50 py-0.5 z-20">
                        {getIdCardCaption()}
                      </div>
                    )}
                    
                    {/* Show video feed OR preview image */}
                    {!previewImageUrl ? (
                      <video 
                        ref={videoRef} 
                        autoPlay 
                        playsInline 
                        muted 
                        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 min-w-full min-h-full object-cover"
                      />
                    ) : (
                      <img 
                        src={previewImageUrl} 
                        alt="Preview" 
                        className="absolute top-0 left-0 w-full h-full object-contain"
                      />
                    )}
                  </div>
                </div>
                
                {/* Controls Container - Fixed at bottom, reduced padding */}
                <div className="bg-gray-900 p-1.5 flex justify-center">
                  {!previewImageUrl ? (
                    /* Camera Button */
                    <button 
                      onClick={captureImage}
                      disabled={isCaptureDisabled()}
                      className={`p-3 rounded-full shadow-lg transition-all ${
                        isCaptureDisabled()
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                          : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-95'
                      }`}
                    >
                      <IconWrapper icon={FiCamera} className="text-lg" />
                    </button>
                  ) : (
                    /* Preview Buttons - compact for small screens */
                    <div className="flex space-x-2">
                      {(captureMode !== 'idcard' || capturedImages.length < 2) && (
                        <button 
                          onClick={handleAddPhoto}
                          className="flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg shadow transition-colors text-xs"
                        >
                          <IconWrapper icon={FiPlus} className="mr-1" />
                          <span>{captureMode === 'idcard' ? 'Next Photo' : 'Add Photo'}</span>
                        </button>
                      )}
                      <button 
                        onClick={handleRetake}
                        className="flex items-center justify-center bg-gray-600 hover:bg-gray-700 text-white px-3 py-1.5 rounded-lg shadow transition-colors text-xs"
                      >
                        <IconWrapper icon={FiRotateCcw} className="mr-1" />
                        <span>Retake</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            <canvas ref={canvasRef} className="hidden" />
          </div>
          
          {/* Thumbnails of captured images - smaller padding */}
          {capturedImages.length > 0 && (
            <div className="bg-white p-1.5 border-t border-gray-200 overflow-y-auto max-h-24">
              <h4 className="font-medium mb-1 flex items-center text-gray-700 text-xs">
                <IconWrapper icon={FiCheck} className="mr-1 text-green-500" />
                {captureMode === 'idcard' ? 'ID Card Images' : 'Captured Images'} ({capturedImages.length}
                {captureMode === 'idcard' && <span className="text-xs text-gray-500 ml-1">/2</span>})
              </h4>
              <div className="flex flex-wrap gap-1.5">
                {capturedImages.map((image, index) => {
                  const thumbnailUrl = URL.createObjectURL(image);
                  return (
                    <div key={index} className="relative w-12 h-12 rounded-md overflow-hidden shadow-sm border border-gray-200 group">
                      <img 
                        src={thumbnailUrl} 
                        alt={`Capture ${index + 1}`} 
                        className="w-full h-full object-cover"
                        data-thumbnail-url={thumbnailUrl}
                      />
                      <button 
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-0 right-0 bg-red-500 text-white text-xs p-0.5 rounded-bl-md"
                        aria-label="Remove image"
                      >
                        <IconWrapper icon={FiX} className="text-xs" />
                      </button>
                      <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-[8px] text-center">
                        {captureMode === 'idcard' ? (index === 0 ? 'Front' : 'Back') : index + 1}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        
        {/* Footer - reduced padding and text size */}
        <div className="p-1.5 border-t border-gray-200 flex justify-between bg-white">
          <button 
            onClick={onClose}
            className="px-2 py-0.5 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 font-medium text-xs"
          >
            Cancel
          </button>
          <button 
            onClick={handleDoneCapturing}
            className={`flex items-center px-2.5 py-0.5 rounded-lg transition-all text-xs ${
              capturedImages.length === 0 || (captureMode === 'idcard' && capturedImages.length !== 2)
                ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                : 'bg-green-600 hover:bg-green-700 text-white shadow-sm'
            }`}
            disabled={capturedImages.length === 0 || (captureMode === 'idcard' && capturedImages.length !== 2)}
          >
            <IconWrapper icon={FiCheck} className="mr-1" />
            <span className="hidden xs:inline">
              {captureMode === 'idcard' ? 'Create ID Card' : `Create PDF (${capturedImages.length})`}
            </span>
            <span className="xs:hidden">
              {captureMode === 'idcard' ? 'Create ID' : `Create (${capturedImages.length})`}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CameraCaptureModal;
